import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import useBlockListFetcher from '../../../../hooks/useBlockListFetcher';
import useBlockAction, { getBlockErrorMessage } from '../../../../hooks/useBlockAction';
import { IP_BLOCKLIST } from '../../../../constants/general';
import { JwtAudienceOptions } from '../../../../constants/AccessLevels';
import * as browserStorage from '../../../../misc/BrowserStorageManager';
import { BottomMargin, Description, StartHeadline } from '../../../../styledComponents/styledComponents';
import {
   BlockStatusDot,
   BlockStatusText,
   BlockTagInput,
   BlockActionButton,
   BlockPanelContent,
   BlockStatusRow,
   BlockCommentLabel,
   BlockErrorText,
} from '../StyledComponents';

function IpBlockPanel({ ip, onBlockAction }) {
   const hasGuardianAccess = browserStorage.getUserInfo().aud.includes(JwtAudienceOptions.GUARDIAN);

   const { fetchBlockList, blockedMap, fetchError } = useBlockListFetcher({ type: IP_BLOCKLIST });
   const { block, unblock, isLoading, isSuccess, error, reset } = useBlockAction({
      valueToBeBlocked: ip,
      type: IP_BLOCKLIST,
   });

   const [tag, setTag] = useState('');

   useEffect(() => {
      fetchBlockList();
   }, [fetchBlockList]);

   useEffect(() => {
      if (isSuccess) {
         fetchBlockList();
         if (onBlockAction) onBlockAction();
         reset();
         setTag('');
      }
   }, [isSuccess, fetchBlockList, onBlockAction, reset]);

   if (!hasGuardianAccess) return null;

   const isBlocked = blockedMap !== undefined ? blockedMap[ip] === true : null;
   const isFetchLoading = isBlocked === null && !fetchError;
   const dimmed = { opacity: 0.5, pointerEvents: 'none' };

   return (
      <BottomMargin pxSize={20}>
         <StartHeadline>IP Blocking</StartHeadline>
         <BlockPanelContent>
            {fetchError ? (
               <BlockStatusText>Failed to load block status.</BlockStatusText>
            ) : (
               <>
                  <BlockStatusRow>
                     {!isFetchLoading && <BlockStatusDot $blocked={isBlocked} />}
                     <BlockStatusText $blocked={isFetchLoading ? undefined : isBlocked}>
                        {isFetchLoading ? 'Checking block status...' : isBlocked ? 'Blocked' : 'Not blocked'}
                     </BlockStatusText>
                  </BlockStatusRow>

                  {(isFetchLoading || !isBlocked) && (
                     <div style={isFetchLoading ? dimmed : undefined}>
                        <Description marginLeftPx={0}>
                           <div>
                              <b>Note:</b> The block only affects new connections or the next reconnect and takes max. 6
                              minutes to be effected.
                           </div>
                           <div>
                              Blocking for a running playout requires a reconnect to take effect. Blocked IPs will be
                              automatically unblocked after 24 hours.
                           </div>
                        </Description>
                        <BlockCommentLabel>Comment (optional)</BlockCommentLabel>
                        <BlockTagInput
                           style={{ width: '100%' }}
                           value={tag}
                           onChange={(e) => setTag(e.target.value)}
                           placeholder='e.g. abuse, suspicious activity...'
                           disabled={isLoading}
                        />
                     </div>
                  )}

                  <div style={isFetchLoading ? dimmed : undefined}>
                     <BlockActionButton
                        $variant={isBlocked ? 'unblock' : 'block'}
                        disabled={isFetchLoading || isLoading}
                        onClick={isBlocked ? unblock : () => block(tag)}
                     >
                        {isLoading
                           ? isBlocked
                              ? 'Unblocking...'
                              : 'Blocking...'
                           : isBlocked
                           ? 'Unblock IP'
                           : 'Block IP'}
                     </BlockActionButton>
                  </div>

                  {error && (
                     <BlockErrorText>
                        {getBlockErrorMessage(
                           error,
                           isBlocked ? 'Unblock failed, please try again.' : 'Block failed, please try again.',
                        )}
                     </BlockErrorText>
                  )}
               </>
            )}
         </BlockPanelContent>
      </BottomMargin>
   );
}

IpBlockPanel.propTypes = {
   ip: PropTypes.string.isRequired,
   onBlockAction: PropTypes.func,
};

export default IpBlockPanel;
